import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Slider } from '@/components/ui/slider';
import { Star, MapPin, DollarSign } from 'lucide-react';
import { categories } from '@/utils/categories';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ArtistListing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    subcategory: searchParams.get('subcategory') || '',
    location: '',
    minPrice: 0,
    maxPrice: 10000,
    minRating: 0,
  });

  useEffect(() => {
    fetchArtists();
  }, [filters]);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.subcategory) params.append('subcategory', filters.subcategory);
      if (filters.location) params.append('location', filters.location);
      if (filters.minPrice > 0) params.append('min_price', filters.minPrice);
      if (filters.maxPrice < 10000) params.append('max_price', filters.maxPrice);
      if (filters.minRating > 0) params.append('min_rating', filters.minRating);

      const response = await axios.get(`${API}/artists?${params.toString()}`);
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      subcategory: '',
      location: '',
      minPrice: 0,
      maxPrice: 10000,
      minRating: 0,
    });
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white" data-testid="artist-listing-page">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-4">
            Browse Artists
          </h1>
          <p className="text-lg text-gray-600">
            Find the perfect talent for your event
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1" data-testid="filters-sidebar">
            <div className="bg-white border border-gray-100 rounded-2xl p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Filters</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  data-testid="clear-filters-button"
                >
                  Clear
                </Button>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="category" className="border-b-0">
                  <AccordionTrigger className="text-sm font-medium">
                    Category
                  </AccordionTrigger>
                  <AccordionContent>
                    <Select
                      value={filters.category}
                      onValueChange={(value) => handleFilterChange('category', value)}
                    >
                      <SelectTrigger data-testid="category-filter">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All Categories</SelectItem>
                        {Object.values(categories).map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </AccordionContent>
                </AccordionItem>

                {filters.category && categories[filters.category]?.subcategories.length > 0 && (
                  <AccordionItem value="subcategory" className="border-b-0">
                    <AccordionTrigger className="text-sm font-medium">
                      Subcategory
                    </AccordionTrigger>
                    <AccordionContent>
                      <Select
                        value={filters.subcategory}
                        onValueChange={(value) => handleFilterChange('subcategory', value)}
                      >
                        <SelectTrigger data-testid="subcategory-filter">
                          <SelectValue placeholder="All Subcategories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Subcategories</SelectItem>
                          {categories[filters.category].subcategories.map((sub) => (
                            <SelectItem key={sub.value} value={sub.value}>
                              {sub.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </AccordionContent>
                  </AccordionItem>
                )}

                <AccordionItem value="location" className="border-b-0">
                  <AccordionTrigger className="text-sm font-medium">
                    Location
                  </AccordionTrigger>
                  <AccordionContent>
                    <Input
                      placeholder="Enter city or state"
                      value={filters.location}
                      onChange={(e) => handleFilterChange('location', e.target.value)}
                      data-testid="location-filter"
                      className="h-10"
                    />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="price" className="border-b-0">
                  <AccordionTrigger className="text-sm font-medium">
                    Price Range
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      <Slider
                        min={0}
                        max={10000}
                        step={100}
                        value={[filters.minPrice, filters.maxPrice]}
                        onValueChange={([min, max]) => {
                          setFilters({ ...filters, minPrice: min, maxPrice: max });
                        }}
                        data-testid="price-slider"
                      />
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>${filters.minPrice}</span>
                        <span>${filters.maxPrice}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="rating" className="border-b-0">
                  <AccordionTrigger className="text-sm font-medium">
                    Minimum Rating
                  </AccordionTrigger>
                  <AccordionContent>
                    <Select
                      value={filters.minRating.toString()}
                      onValueChange={(value) => handleFilterChange('minRating', parseFloat(value))}
                    >
                      <SelectTrigger data-testid="rating-filter">
                        <SelectValue placeholder="Any Rating" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Any Rating</SelectItem>
                        <SelectItem value="3">3+ Stars</SelectItem>
                        <SelectItem value="4">4+ Stars</SelectItem>
                        <SelectItem value="4.5">4.5+ Stars</SelectItem>
                      </SelectContent>
                    </Select>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </aside>

          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading artists...</p>
              </div>
            ) : artists.length === 0 ? (
              <div className="text-center py-12" data-testid="no-artists-message">
                <p className="text-gray-600">No artists found matching your criteria</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="artists-grid">
                {artists.map((artist) => (
                  <Link
                    key={artist.id}
                    to={`/artists/${artist.id}`}
                    className="group"
                    data-testid={`artist-card-${artist.id}`}
                  >
                    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover-lift">
                      <div className="relative h-64">
                        <img
                          src={
                            artist.portfolio_images[0] ||
                            categories[artist.category]?.image ||
                            'https://images.unsplash.com/photo-1681149342107-b39e06d03f6b?crop=entropy&cs=srgb&fm=jpg&q=85'
                          }
                          alt={artist.artist_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {artist.rating > 0 && (
                          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm font-semibold">{artist.rating}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {artist.artist_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 capitalize">
                          {categories[artist.category]?.name || artist.category}
                          {artist.subcategory && ` • ${artist.subcategory}`}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-4 h-4" />
                            <span>{artist.location}</span>
                          </div>
                          <div className="flex items-center space-x-1 font-semibold text-purple-600">
                            <DollarSign className="w-4 h-4" />
                            <span>From ${artist.price_starting}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistListing;
